import React, {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {ParamsModelPrijavaKvara, PrijavaKvaraModel} from '../../models/PrijavaKvara'
import {getAllPrijavaKvara, getSredstvoInfo, savePrijavaKvara} from '../../api/prijavaKvara'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import notifyToast from '../../components/Toast/Toast'
import {ToastContainer} from 'react-toastify'
import TitleComponent from '../../components/TitleComponent/TitleComponent'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import clsx from 'clsx'
import {flexRender, getCoreRowModel, getFilteredRowModel, useReactTable} from '@tanstack/react-table'
import moments from 'moment'
import TablePagination from '../../components/TablePagination/TablePagination'
import {PaginationModel} from '../../models/PaginationModel'
import useDebounce from '../../hooks/useDebounce'

interface KvaraDetailsModel {
    invBroj?: string
    opisKvara?: string
    urgentnaPrijava?: string
}

const PrijavaKvaraDetails = () => {
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()

    const initialValues: KvaraDetailsModel = {
        invBroj: undefined,
        opisKvara: undefined,
        urgentnaPrijava: undefined,
    }

    const [pagination, setpagination] = useState<PaginationModel>({
        curPage: 1,
        totalPage: 1,
        perPage: 10,
    })

    const [message, setmessage] = useState<string>()
    const [initialData, setInitialData] = useState<KvaraDetailsModel>(initialValues)
    const [params, setparams] = useState<ParamsModelPrijavaKvara>({per_page: 10, page: 1, active: 1})

    const [tableData, settableData] = useState<PrijavaKvaraModel[]>([])

    const updatePage = () => {
        setLoading(true)
        getAllPrijavaKvara(params)
            .then((response) => {
                const data = response.data['data']
                settableData(data)

                // if your API returns pagination fields, you can set them here (optional)
                // keeping your existing pattern; not changing logic.
            })
            .catch((error) => {
                console.error('Error: ', error)
                notifyToast(false, error)
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        updatePage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params])

    const validationSchema = Yup.object({
        invBroj: Yup.string().required('Obavezno polje'),
        opisKvara: Yup.string().required('Obavezno polje'),
        urgentnaPrijava: Yup.string().required('Obavezno polje'),
    })

    const formik = useFormik({
        initialValues: initialData,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values, {setStatus, setSubmitting}) => {
            setLoading(true)
            try {
                const response = await savePrijavaKvara(values)

                setTimeout(() => {
                    navigate(-1)
                }, 3000)

                if (response.error === 0) {
                    notifyToast(true, response.message)
                } else {
                    notifyToast(false, response.message || 'An unexpected error occurred.')
                }
            } catch (error: any) {
                console.error('Error: ', error)
                notifyToast(false, error?.response?.data?.message ?? 'Greška')
                setStatus(error)
            } finally {
                setLoading(false)
                setSubmitting(false)
            }
        },
    })

    const invBroj = useDebounce(formik.values.invBroj)

    useEffect(() => {
        if (invBroj) {
            const params = {invBroj: invBroj}
            getSredstvoInfo(params).then((response: any) => {
                if (response?.SredstvoNaziv) {
                    setmessage(response.SredstvoNaziv)
                } else {
                    setmessage(undefined)
                }
            })
        } else {
            setmessage(undefined)
        }
    }, [invBroj])

    const columns = [
        {id: 'Id', header: 'ID', accessorKey: 'PrijavaId'},
        {id: 'Datum', header: 'Datum', accessorFn: (row) => `${moments(row.PrijavaDatum).format('DD-MM-YYYY')}`},
        {id: 'Inv broj.', header: 'Inv. broj', accessorKey: 'PrijavaInvBroj'},
        {id: 'Sredstvo naziv', header: 'Sredstvo', accessorKey: 'sredstvo.SredstvoNaziv'},
        {id: 'Opis kvara', header: 'Opis kvara', accessorKey: 'PrijavaOpisKvara'},
        {id: 'Kreirao prijavu', header: 'Kreirao', accessorKey: 'uposlenik.UposlenikImePrezime'},
        {id: 'Datum / Vrijeme', header: 'Vrijeme', accessorFn: (row) => `${moments(row.PrijavaDatum).format('DD-MM-YYYY  HH:mm:ss')}`},
        {
            id: 'Status',
            header: 'Status',
            accessorKey: 'Status',
            cell: ({row}: {row: any}) => {
                const boja = row.original?.status?.Boja || '#6c757d'
                const naziv = row.original?.status?.Naziv || 'Nepoznato'
                return (
                    <span
                        className='badge rounded-pill px-3 py-2 fw-semibold'
                        style={{backgroundColor: boja, color: '#fff', display: 'inline-block', minWidth: 120, textAlign: 'center'}}
                        title={naziv}
                    >
            {naziv}
          </span>
                )
            },
        },
    ]

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    })

    const assetFound = !!message
    const canSubmit = !loading && !formik.isSubmitting && formik.isValid && assetFound

    return (
        <div className='card mb-5 mb-xl-10'>
            <ToastContainer />

            <div
                className='card-header border-0 cursor-pointer'
                role='button'
                data-bs-target='#kt_account_profile_details'
                aria-expanded='true'
                aria-controls='kt_account_profile_details'
            >
                <div className='card-title m-0 d-flex flex-column'>
                    <TitleComponent name='Prijava kvara' />
                    <div className='text-muted fs-7 mt-1'>Popunite formu ispod — sistem će pronaći sredstvo po inventurnom broju.</div>
                </div>
            </div>

            {/* Helpful “How to” */}
            <div className='px-9 pt-4'>
                <div className='alert alert-info mb-4'>
                    <div className='fw-bold mb-1'>Kako unijeti prijavu:</div>
                    <ol className='mb-0 ps-3'>
                        <li>Upišite <b>inventurni broj</b> sredstva.</li>
                        <li>Provjerite da se pojavi naziv sredstva (zelena poruka).</li>
                        <li>Opišite kvar (što detaljnije).</li>
                        <li>Označite da li je prijava urgentna.</li>
                        <li>Kliknite <b>Potvrdi</b>.</li>
                    </ol>
                </div>
            </div>

            <div className='row g-0'>
                {/* LEFT: FORM */}
                <div id='kt_account_profile_details' className='collapse show col-lg-6 border-end'>
                    {loading ? (
                        <div className='p-9'>
                            <LoadingComponent />
                        </div>
                    ) : (
                        <form onSubmit={formik.handleSubmit} noValidate className='form'>
                            <div className='card-body border-top p-9'>
                                {/* Step 1 */}
                                <div className='mb-6'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <label className='col-form-label required fw-bold fs-6 mb-2'>
                                            1) Inventurni broj sredstva
                                        </label>
                                        <span className='badge bg-light text-dark border'>Korak 1</span>
                                    </div>

                                    <input
                                        type='text'
                                        placeholder='Npr. 100234'
                                        {...formik.getFieldProps('invBroj')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            {'is-invalid': formik.touched.invBroj && formik.errors.invBroj},
                                            {'is-valid': formik.touched.invBroj && !formik.errors.invBroj}
                                        )}
                                    />

                                    <div className='form-text'>
                                        Upišite tačan broj sa naljepnice / evidencije. Nakon unosa, sistem automatski traži sredstvo.
                                    </div>

                                    {formik.touched.invBroj && formik.errors.invBroj && (
                                        <div className='fv-plugins-message-container mt-2'>
                                            <div className='fv-help-block text-danger'>{formik.errors.invBroj}</div>
                                        </div>
                                    )}

                                    {/* Asset feedback */}
                                    <div className='mt-3'>
                                        {formik.values.invBroj ? (
                                            assetFound ? (
                                                <div className='alert alert-success d-flex align-items-center mb-0 py-3'>
                                                    <div className='me-3'>
                                                        <span className='badge bg-success'>OK</span>
                                                    </div>
                                                    <div>
                                                        <div className='fw-bold'>Sredstvo pronađeno:</div>
                                                        <div className='fs-6'>{message}</div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className='alert alert-warning d-flex align-items-center mb-0 py-3'>
                                                    <div className='me-3'>
                                                        <span className='badge bg-warning text-dark'>PAŽNJA</span>
                                                    </div>
                                                    <div>
                                                        <div className='fw-bold'>Sredstvo nije pronađeno</div>
                                                        <div className='fs-7 text-muted'>Provjerite inventurni broj (da li ste pogriješili jednu cifru).</div>
                                                    </div>
                                                </div>
                                            )
                                        ) : null}
                                    </div>
                                </div>

                                {/* Step 2 */}
                                <div className='mb-6'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <label className='col-form-label required fw-bold fs-6 mb-2'>
                                            2) Opis kvara
                                        </label>
                                        <span className='badge bg-light text-dark border'>Korak 2</span>
                                    </div>

                                    <textarea
                                        rows={5}
                                        placeholder='Npr. Motor ne funkcionise..'
                                        {...formik.getFieldProps('opisKvara')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            {'is-invalid': formik.touched.opisKvara && formik.errors.opisKvara},
                                            {'is-valid': formik.touched.opisKvara && !formik.errors.opisKvara}
                                        )}
                                    />

                                    <div className='form-text'>Što više detalja unesete, brže će se kvar riješiti.</div>

                                    {formik.touched.opisKvara && formik.errors.opisKvara && (
                                        <div className='fv-plugins-message-container mt-2'>
                                            <div className='fv-help-block text-danger'>{formik.errors.opisKvara}</div>
                                        </div>
                                    )}
                                </div>

                                {/* Step 3 */}
                                <div className='mb-2'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <label className='fw-bold fs-6 required mb-2'>3) Da li je urgentno?</label>
                                        <span className='badge bg-light text-dark border'>Korak 3</span>
                                    </div>

                                    <div className='row g-3'>
                                        <div className='col-6'>
                                            <label
                                                htmlFor='Da'
                                                className={clsx(
                                                    'w-100 border rounded p-4 d-flex align-items-center gap-3 cursor-pointer',
                                                    formik.values.urgentnaPrijava === '1' ? 'border-danger bg-light' : 'border-gray-300'
                                                )}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <input
                                                    id='Da'
                                                    type='radio'
                                                    value='1'
                                                    name='urgentna'
                                                    onChange={(e) => formik.setFieldValue('urgentnaPrijava', e.target.value)}
                                                    checked={formik.values.urgentnaPrijava === '1'}
                                                />
                                                <div>
                                                    <div className='fw-bold'>Da</div>
                                                    <div className='text-muted fs-7'>Zaustavlja proizvodnju / veliki rizik.</div>
                                                </div>
                                            </label>
                                        </div>

                                        <div className='col-6'>
                                            <label
                                                htmlFor='Ne'
                                                className={clsx(
                                                    'w-100 border rounded p-4 d-flex align-items-center gap-3 cursor-pointer',
                                                    formik.values.urgentnaPrijava === '0' ? 'border-success bg-light' : 'border-gray-300'
                                                )}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <input
                                                    id='Ne'
                                                    type='radio'
                                                    value='0'
                                                    name='urgentna'
                                                    onChange={(e) => formik.setFieldValue('urgentnaPrijava', e.target.value)}
                                                    checked={formik.values.urgentnaPrijava === '0'}
                                                />
                                                <div>
                                                    <div className='fw-bold'>Ne</div>
                                                    <div className='text-muted fs-7'>Može sačekati / nije kritično.</div>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    {formik.touched.urgentnaPrijava && formik.errors.urgentnaPrijava && (
                                        <div className='fv-plugins-message-container mt-2'>
                                            <div className='fv-help-block text-danger'>{formik.errors.urgentnaPrijava}</div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className='card-footer d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 py-6 px-9'>
                                <div className='text-muted fs-7'>
                                    {!assetFound && formik.values.invBroj ? (
                                        <span>Ne možete potvrditi dok se ne pronađe sredstvo po inventurnom broju.</span>
                                    ) : (
                                        <span>Provjerite unesene podatke prije potvrde.</span>
                                    )}
                                </div>

                                <button type='submit' className='btn btn-primary btn-lg' disabled={!canSubmit}>
                                    {!loading && 'Potvrdi'}
                                    {loading && (
                                        <span className='indicator-progress' style={{display: 'block'}}>
                      Molimo sačekajte...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* RIGHT: TABLE */}
                <div className='col-lg-6'>
                    {loading ? (
                        <div className='p-9'>
                            <LoadingComponent />
                        </div>
                    ) : (
                        <div className='p-9'>
                            <div className='d-flex align-items-center justify-content-between mb-3'>
                                <div>
                                    <div className='fw-bold'>Pregled prijava</div>
                                    <div className='text-muted fs-7'>Najnovije prijave (lista).</div>
                                </div>
                            </div>

                            <div className='table-responsive'>
                                <table className='table table-bordered table-striped table-hover align-middle mb-3'>
                                    <thead
                                        style={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            background: '#212529',
                                            color: '#fff',
                                        }}
                                    >
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <tr key={headerGroup.id} className='text-uppercase fw-bolder fs-7'>
                                            {headerGroup.headers.map((header) => (
                                                <th
                                                    className='text-white py-3'
                                                    key={header.id}
                                                    style={{whiteSpace: 'nowrap'}}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                    </thead>

                                    <tbody className='text-gray-800'>
                                    {table.getRowModel().rows && table.getRowModel().rows.length > 0 ? (
                                        table.getRowModel().rows.map((row) => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map((cell) => (
                                                    <td key={cell.id} className='py-3'>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={8}>
                                                <div className='d-flex text-center w-100 align-content-center justify-content-center py-4'>
                                                    Nema podataka.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            <TablePagination
                                updatePage={(page, per_page) => setparams({...params, page: page, per_page: per_page})}
                                pagination={pagination}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default PrijavaKvaraDetails
